import {useEffect, useRef} from 'react';

const IDLE_TIMEOUT_MINUTES = 20;
const HEARTBEAT_INTERVAL_MINUTES = 5;
const UTM_EXPIRY_MINUTES = 20;

const getOrResetSessionIdAndUtmParams = () => {
    const now = Date.now();
    const expiryMs = UTM_EXPIRY_MINUTES * 60 * 1000;

    const storedUtm = sessionStorage.getItem("utmParams");
    const storedAt = sessionStorage.getItem("utmSavedAt");
    const isExpired = !storedUtm || !storedAt || (now - parseInt(storedAt, 10)) > expiryMs;

    if (isExpired) {
        // Expired — reset session and UTM
        sessionStorage.removeItem("session_id");
        sessionStorage.removeItem("utmParams");
        sessionStorage.removeItem("utmSavedAt");

        const searchParams = new URLSearchParams(window.location.search);
        const utmParams = {
            utm_source: searchParams.get("utm_source") || '',
            utm_medium: searchParams.get("utm_medium") || '',
            utm_campaign: searchParams.get("utm_campaign") || '',
            utm_content: searchParams.get("utm_content") || '',
            utm_term: searchParams.get("utm_term") || ''
        };

        const newSessionId = crypto.randomUUID();
        sessionStorage.setItem("session_id", newSessionId);
        sessionStorage.setItem("utmParams", JSON.stringify(utmParams));
        sessionStorage.setItem("utmSavedAt", now.toString());

        return {sessionId: newSessionId, utmParams};
    }

    return {
        sessionId: sessionStorage.getItem("session_id"),
        utmParams: JSON.parse(storedUtm)
    };
};

const SessionManagement = ({organisation, destinationGroup, _restServicePost}) => {
    const {sessionId: storedSessionId, utmParams: storedUtmParams} = getOrResetSessionIdAndUtmParams();

    const sessionId = useRef(storedSessionId);
    const utmParams = useRef(storedUtmParams);
    const sessionStartTime = useRef(new Date().toISOString());
    const sessionEnded = useRef(false);
    const idleTimeout = useRef(null);
    const heartbeatInterval = useRef(null);

    const sendSessionEnd = (reason = "unload") => {
        if (sessionEnded.current) return;
        sessionEnded.current = true;

        const payload = {
            session_id: sessionId.current,
            organisation_id: organisation.id,
            destination_group_id: destinationGroup.id,
            session_end: new Date().toISOString(),
            end_reason: reason
        };

        navigator.sendBeacon(
            process.env.REACT_APP_API_BASE_URL + 'addSessionInformation',
            new Blob([JSON.stringify(payload)], {type: 'application/json'})
        );
    };

    const sendHeartbeat = () => {
        const payload = {
            session_id: sessionId.current,
            timestamp: new Date().toISOString()
        };
        _restServicePost('sessionHeartbeat', payload)
            .then(() => console.log("Heartbeat sent"))
            .catch((err) => console.error("Heartbeat failed", err));
    };

    const resetIdleTimer = () => {
        clearTimeout(idleTimeout.current);
        idleTimeout.current = setTimeout(() => {
            console.log("Idle timeout, ending session");
            sendSessionEnd("idle_timeout");
        }, IDLE_TIMEOUT_MINUTES * 60 * 1000);
    };

    useEffect(() => {
        if (!organisation?.id || !destinationGroup?.id) return;

        const payload = {
            session_id: sessionId.current,
            organisation_id: organisation.id,
            destination_group_id: destinationGroup.id,
            club_code: organisation.clubCode || '',
            venue_id: organisation.venueId || null,
            session_start: sessionStartTime.current,
            okta_id: null,
            campaign_id: utmParams.current.utm_campaign,
            campaign_source: utmParams.current.utm_source,
            campaign_medium: utmParams.current.utm_medium,
            campaign_name: utmParams.current.utm_content,
            campaign_term: utmParams.current.utm_term
        };

        _restServicePost('addSessionInformation', payload)
            .then(() => console.log("Session start logged:", sessionId.current))
            .catch((error) => console.error("Session logging failed", error));

        const unloadHandler = () => sendSessionEnd("unload");

        const visibilityHandler = () => {
            if (document.visibilityState === "hidden") {
                sendSessionEnd("visibility_hidden");
            }
        };

        window.addEventListener("beforeunload", unloadHandler);
        window.addEventListener("unload", unloadHandler);
        document.addEventListener("visibilitychange", visibilityHandler);

        const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
        activityEvents.forEach(e => window.addEventListener(e, resetIdleTimer));
        resetIdleTimer();

        sendHeartbeat();
        heartbeatInterval.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MINUTES * 60 * 1000);

        return () => {
            clearTimeout(idleTimeout.current);
            clearInterval(heartbeatInterval.current);
            window.removeEventListener("beforeunload", unloadHandler);
            window.removeEventListener("unload", unloadHandler);
            document.removeEventListener("visibilitychange", visibilityHandler);
            activityEvents.forEach(e => window.removeEventListener(e, resetIdleTimer));
        };
    }, [organisation, destinationGroup, _restServicePost]);

    return null;
};

export default SessionManagement;
